import { ACTION_CATALOG } from "../features/command-palette/services/catalog";
import type { DeckorateCommand } from "../types";

/** Asset Library is opened via DeckorateModule's special-cased
 * `openAssetLibrary()` call, not a tab-based catalogue command — see its
 * `navigate()`. Code Mode's dispatch mirrors that same special case. */
export type CodeModeTarget = { readonly kind: "command"; readonly command: DeckorateCommand } | { readonly kind: "asset-library" };

export interface CodeModeEntry {
  readonly code: string;
  readonly label: string;
  readonly requiresSelection: boolean;
  readonly target: CodeModeTarget;
}

const ASSET_LIBRARY_ENTRY: CodeModeEntry = {
  code: "L",
  label: "Asset Library",
  requiresSelection: false,
  target: { kind: "asset-library" },
};

function hasCode(cmd: DeckorateCommand): cmd is DeckorateCommand & { code: string } {
  return !!cmd.code;
}

/**
 * The single Code Mode lookup table: every catalogue command carrying a
 * `code` (see catalog.ts), plus the Asset Library special case. Built once
 * from ACTION_CATALOG rather than hand-maintained, so a code added there is
 * live here automatically.
 */
export const CODE_MODE_ENTRIES: readonly CodeModeEntry[] = [
  ...ACTION_CATALOG.filter(hasCode).map(
    (cmd): CodeModeEntry => ({
      code: cmd.code,
      label: cmd.label,
      requiresSelection: !!cmd.requiresSelection,
      target: { kind: "command", command: cmd },
    }),
  ),
  ASSET_LIBRARY_ENTRY,
];

function catalogCommand(id: string): DeckorateCommand {
  const cmd = ACTION_CATALOG.find((entry) => entry.id === id);
  if (!cmd) throw new Error(`codeMode: catalog command "${id}" is missing`);
  return cmd;
}

function spacingEntry(id: string, arrow: string): CodeModeEntry {
  const cmd = catalogCommand(id);
  return {
    code: `S${arrow}`,
    label: cmd.label,
    requiresSelection: !!cmd.requiresSelection,
    target: { kind: "command", command: cmd },
  };
}

/**
 * The "S then arrow" spacing gesture: → widens and ← tightens horizontally,
 * ↑/↓ do the same vertically.
 *
 * It cannot live in CODE_MODE_ENTRIES like everything else. That table is
 * built from commands carrying a `code`, and matching walks a buffer of
 * letters — but an arrow key has no `KeyA`-`KeyZ` `event.code`, so it can
 * never be accumulated into a buffer at all. store.ts therefore special-cases
 * it: with the buffer sitting on exactly "S", the next arrow resolves here.
 *
 * The four spacing commands deliberately keep no `code` of their own in
 * catalog.ts — reaching them is this gesture, not a two-letter mnemonic — so
 * they're looked up by id instead. Every other S* code (SC, SL, SR, ST, SB,
 * SH, SV, SG, SW) is untouched and still resolves as an ordinary second
 * letter.
 */
const SPACING_BY_ARROW: Readonly<Record<string, CodeModeEntry>> = {
  ArrowRight: spacingEntry("format-increaseHorizSpace", "→"),
  ArrowLeft: spacingEntry("format-decreaseHorizSpace", "←"),
  ArrowUp: spacingEntry("format-increaseVertSpace", "↑"),
  ArrowDown: spacingEntry("format-decreaseVertSpace", "↓"),
};

/** The spacing action for an arrow pressed after a bare "S", or null for any
 * other key. Takes `KeyboardEvent.code`, matching how store.ts reads keys. */
export function spacingEntryForArrow(eventCode: string): CodeModeEntry | null {
  return SPACING_BY_ARROW[eventCode] ?? null;
}

